import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

import { GetReportBoundaryProjectsDetailResData } from '@/apis/report/boundary-projects/detail.dto';
import { BoundaryProjectAssignmentStatus } from '@/apis/report/boundary-projects/users/assignment-status.dto';
import { getEffectiveRoleForProject } from '@/routes/utils';
import { Role } from '@/typings/user';
import { CustomStateCreator } from '@/typings/zustand';
import { isEnabledReduxDevTool } from '@/utils/common';
import { immer } from '@/zustand/middlewares/immer';

import { useUserStore } from '../useUserStore';

interface PageSlice {
  projectInfo: GetReportBoundaryProjectsDetailResData | null;
  setProjectInfo: (projectInfo: GetReportBoundaryProjectsDetailResData | null) => void;
  projectAssignmentStatus: BoundaryProjectAssignmentStatus | null;
  setProjectAssignmentStatus: (projectAssignmentStatus: Partial<BoundaryProjectAssignmentStatus> | null) => void;
  getProjectEffectiveRole: () => Role;
}

type CombinedSlice = PageSlice;

const createPageSlice: CustomStateCreator<CombinedSlice, PageSlice> = (set, get) => ({
  projectInfo: null,
  setProjectInfo: (projectInfo) => {
    set(
      (state) => {
        if (projectInfo) {
          state.projectInfo = projectInfo as GetReportBoundaryProjectsDetailResData;
        }
      },
      false,
      'setProjectInfo',
    );
  },
  projectAssignmentStatus: null,
  setProjectAssignmentStatus: (projectAssignmentStatus) => {
    set(
      (state) => {
        state.projectAssignmentStatus = {
          ...state.projectAssignmentStatus,
          ...projectAssignmentStatus,
        } as BoundaryProjectAssignmentStatus;
      },
      false,
      'setProjectAssignmentStatus',
    );
  },
  getProjectEffectiveRole: () => {
    const { userInfo } = useUserStore.getState();
    return getEffectiveRoleForProject(userInfo?.role as Role, get().projectAssignmentStatus?.isProjectOwner);
  },
});

export const useBoundaryProjectStore = create<CombinedSlice>()(
  immer(
    devtools(
      subscribeWithSelector((...a) => ({
        ...createPageSlice(...a),
      })),
      { enabled: isEnabledReduxDevTool, name: 'useBoundaryProjectStore' },
    ),
  ),
);
